import { useState, useMemo, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { MatchDetailResponse } from '../../../../../api/useSupplierReconciliation/useMatchDetail'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import moment from 'moment'
import { getUserTimezone, formatters } from '@utils/intl/dateTimeFormatters'
import { useFormatter } from '@api/useFormatter'
import { addCommasToNumber } from '@gravitate-js/excalibrr'
import type { LabeledValue } from 'antd/es/select'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Custom hook for managing match detail state
 * Separates state management from UI components
 */
export function useMatchDetailState(matchDetails: MatchDetailResponse | undefined) {
  const queryClient = useQueryClient()
  const { formatCurrency: formatCurrencyHelper } = useFormatter()
  const currencySymbol = formatCurrencyHelper(null, true)
  const [approvePopoverOpen, setApprovePopoverOpen] = useState(false)
  const [selectedReasonCode, setSelectedReasonCode] = useState('')
  const [holdPopoverOpen, setHoldPopoverOpen] = useState(false)
  const [selectedHoldReason, setSelectedHoldReason] = useState<LabeledValue | null>(null)

  // Dynamic currency formatter using market profile currency symbol
  const formatCurrency = useCallback(
    (amount?: number): string => {
      if (amount === null || amount === undefined) return `${currencySymbol}0.00`
      return `${currencySymbol}${addCommasToNumber(amount, 2)}`
    },
    [currencySymbol]
  )

  // Computed values
  const isMatchApproved = matchDetails?.status === 'approved'
  const isMatchUnmatched = matchDetails?.status === 'unmatched'
  const containsInvoice = matchDetails?.document_detail_rows?.some((row) => row.document_type === 'invoice')

  // Get invoice document details
  const invoiceDocument = useMemo(() => {
    return matchDetails?.document_detail_rows?.find((row) => row.document_type === 'invoice')
  }, [matchDetails])

  // Get BOL document details
  const bolDocument = useMemo(() => {
    return matchDetails?.document_detail_rows?.find((row) => row.document_type === 'bol')
  }, [matchDetails])

  // Format date helper - shows only date without time
  const formatDate = useCallback((dateStr: string | null | undefined) => {
    if (!dateStr) return 'N/A'
    return dayjs.utc(dateStr).tz(getUserTimezone()).format('MMM DD, YYYY')
  }, [])

  // Format date with time and timezone helper - matches the grid display
  const formatDateTime = useCallback(
    (dateStr: string | null | undefined) => {
      if (!dateStr) return 'N/A'

      // Use API timezone data - priority: bol_date_local, then timezone_data.terminal
      const timezone = matchDetails?.bol_date_local || matchDetails?.timezone_data?.terminal

      // CRITICAL: Parse as UTC first (like MatchView dateColumns do)
      // API returns UTC timestamps without 'Z' suffix, so we must parse as UTC
      // Use false for overrideSelectedTime to respect Control Panel settings
      return formatters.DATE_TIME(moment.utc(dateStr), timezone, false) || 'N/A'
    },
    [matchDetails?.bol_date_local, matchDetails?.timezone_data?.terminal]
  )

  // Get status configuration
  const getStatusConfig = useCallback((status: string) => {
    const configs: Record<string, any> = {
      approved: {
        display: 'Approved',
        icon: 'check_circle',
        tagProps: { success: true },
      },
      unmatched: {
        display: 'Unmatched',
        icon: 'help',
        tagProps: { warning: true },
      },
      unapproved: {
        display: 'Unapproved',
        icon: 'handshake',
        tagProps: { warning: true },
      },
      hold: {
        display: 'On Hold',
        icon: 'pause',
        tagProps: { warning: true },
      },
    }
    return configs[status] || { display: 'Unknown', icon: 'help', tagProps: {} }
  }, [])

  // Invalidate match detail query
  const invalidateMatchDetail = useCallback(
    (matchId: string) => {
      queryClient.invalidateQueries({
        queryKey: [
          'post',
          '/supplier_rec/match/detail',
          {
            params: {
              query: {
                match_id: matchId,
              },
            },
          },
        ],
      })
    },
    [queryClient]
  )

  return {
    // State
    approvePopoverOpen,
    setApprovePopoverOpen,
    selectedReasonCode,
    setSelectedReasonCode,
    holdPopoverOpen,
    setHoldPopoverOpen,
    selectedHoldReason,
    setSelectedHoldReason,

    // Computed values
    isMatchApproved,
    isMatchUnmatched,
    containsInvoice,
    invoiceDocument,
    bolDocument,

    // Helpers
    formatCurrency,
    formatDate,
    formatDateTime,
    getStatusConfig,
    invalidateMatchDetail,
  }
}
